import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import Alert from '@/components/ui/alert';
const AlertDescription = ({ children, ...props }) => (
  <div className="alert-description" {...props}>{children}</div>
);
import { Badge } from '@/components/ui/badge';
import { vectorStore } from '@/lib/ai/vector-store-provider';
import { Database, AlertCircle, CheckCircle, RefreshCw, HelpCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface RAGDebuggerProps {
  onClose?: () => void;
}

export function RAGDebugger({ onClose }: RAGDebuggerProps) {
  const [isReady, setIsReady] = useState<boolean>(false);
  const [documentCount, setDocumentCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [checks, setChecks] = useState<{
    vectorStoreExists: boolean;
    isReadyMethodExists: boolean;
    documentCountMethodExists: boolean;
    openAIConfigured: boolean;
  }>({
    vectorStoreExists: false,
    isReadyMethodExists: false,
    documentCountMethodExists: false,
    openAIConfigured: false,
  });
  
  const { toast } = useToast();

  // Check RAG status on mount
  useEffect(() => {
    checkRAGStatus();
  }, []);

  const checkRAGStatus = async () => {
    setIsLoading(true);
    try {
      // Check if vector store exists
      const vectorStoreExists = typeof vectorStore !== 'undefined';
      
      // Check if methods exist
      const isReadyMethodExists = vectorStoreExists && typeof vectorStore.isReady === 'function';
      const documentCountMethodExists = vectorStoreExists && typeof vectorStore.getDocumentCount === 'function';
      
      // Check if OpenAI is configured (assuming there's an openAIService export)
      const openAIConfigured = false; // We'll implement this properly if needed
      
      setChecks({
        vectorStoreExists,
        isReadyMethodExists,
        documentCountMethodExists,
        openAIConfigured,
      });
      
      // Get the actual status
      if (isReadyMethodExists) {
        const ready = vectorStore.isReady();
        setIsReady(ready);
      }
      
      if (documentCountMethodExists) {
        const count = vectorStore.getDocumentCount();
        setDocumentCount(count);
      }
      
      console.log("[RAG Debugger] Status checks completed:", {
        vectorStoreExists,
        isReadyMethodExists,
        documentCountMethodExists,
        isReady: isReadyMethodExists ? vectorStore.isReady() : 'unknown',
        documentCount: documentCountMethodExists ? vectorStore.getDocumentCount() : 'unknown',
      });
      
    } catch (error) {
      console.error("[RAG Debugger] Error checking RAG status:", error);
      toast({
        variant: 'destructive',
        title: 'Error Checking RAG Status',
        description: error instanceof Error ? error.message : 'An unknown error occurred',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const loadSampleData = async () => {
    setIsLoading(true);
    try {
      // Simple sample data
      const sampleData = `
# FlexHelp Property Management Knowledge Base

## Property Maintenance
Regular property maintenance is essential for preserving value and ensuring tenant satisfaction. Key maintenance tasks include:
- Checking HVAC systems quarterly
- Inspecting roofs and gutters twice yearly
- Testing smoke and carbon monoxide detectors monthly
- Scheduling professional pest control every 3-6 months
- Servicing water heaters annually

## Tenant Relations
Building positive tenant relationships helps reduce turnover and minimize disputes. Best practices include:
- Responding to maintenance requests within 24-48 hours
- Providing multiple communication channels for tenants
- Conducting move-in and move-out inspections with tenants present
- Sending renewal notices 60-90 days before lease expiration
- Offering online payment options for convenience
      `;
      
      if (typeof vectorStore.loadTrainingData === 'function') {
        await vectorStore.loadTrainingData(sampleData);
        
        // Update status after loading
        setIsReady(vectorStore.isReady());
        setDocumentCount(vectorStore.getDocumentCount());
        
        toast({
          title: 'Sample Data Loaded',
          description: `Successfully loaded sample data. Now have ${vectorStore.getDocumentCount()} document chunks.`,
        });
      } else {
        throw new Error('loadTrainingData method not found on vectorStore');
      }
    } catch (error) {
      console.error("[RAG Debugger] Error loading sample data:", error);
      toast({
        variant: 'destructive',
        title: 'Error Loading Sample Data',
        description: error instanceof Error ? error.message : 'An unknown error occurred',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" /> 
          RAG System Debugger
        </CardTitle>
        <CardDescription>
          Diagnose and fix issues with the Retrieval-Augmented Generation system
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status Overview */}
        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-3">
            {isReady ? (
              <CheckCircle className="h-6 w-6 text-green-500" />
            ) : (
              <AlertCircle className="h-6 w-6 text-amber-500" />
            )}
            <div>
              <h3 className="font-medium">RAG System Status</h3>
              <p className="text-sm text-muted-foreground">
                {isReady 
                  ? `Ready with ${documentCount} document chunks` 
                  : "Not initialized or empty"}
              </p>
            </div>
          </div>
          <Badge variant={isReady ? "secondary" : "outline"} className={isReady ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300" : ""}>
            {isReady ? "READY" : "NOT READY"}
          </Badge>
        </div>
        
        {/* System Checks */}
        <div className="space-y-2">
          <h3 className="font-medium text-sm mb-2">System Checks</h3>
          
          <div className="grid grid-cols-1 gap-2">
            <div className="flex items-center justify-between p-2 bg-card rounded-md border">
              <span className="text-sm">Vector Store Available</span>
              <Badge variant={checks.vectorStoreExists ? "secondary" : "destructive"} className={checks.vectorStoreExists ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300" : ""}>
                {checks.vectorStoreExists ? "YES" : "NO"}
              </Badge>
            </div>
            
            <div className="flex items-center justify-between p-2 bg-card rounded-md border">
              <span className="text-sm">isReady() Method Available</span>
              <Badge variant={checks.isReadyMethodExists ? "secondary" : "destructive"} className={checks.isReadyMethodExists ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300" : ""}>
                {checks.isReadyMethodExists ? "YES" : "NO"}
              </Badge>
            </div>
            
            <div className="flex items-center justify-between p-2 bg-card rounded-md border">
              <span className="text-sm">getDocumentCount() Method Available</span>
              <Badge variant={checks.documentCountMethodExists ? "secondary" : "destructive"} className={checks.documentCountMethodExists ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300" : ""}>
                {checks.documentCountMethodExists ? "YES" : "NO"}
              </Badge>
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="space-y-2">
          <h3 className="font-medium text-sm mb-2">Actions</h3>
          
          <div className="flex flex-wrap gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={checkRAGStatus}
              disabled={isLoading}
              className="flex items-center gap-1"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh Status
            </Button>
            
            <Button 
              variant="default" 
              size="sm" 
              onClick={loadSampleData}
              disabled={isLoading || !checks.vectorStoreExists}
              className="flex items-center gap-1"
            >
              <Database className="h-4 w-4" />
              Load Sample Data
            </Button>
          </div>
        </div>
        
        {/* Troubleshooting Guide */}
        {!isReady && (
          <Alert>
            <HelpCircle className="h-4 w-4" />
            <h4 className="font-medium mb-1">Troubleshooting Tips</h4>
            <AlertDescription>
              <ul className="text-sm list-disc pl-4 space-y-1 mt-1">
                <li>Check if the vector store provider is properly initialized</li>
                <li>Upload training data through the settings panel</li>
                <li>Try the "Load Sample Data" button above to test the system</li>
                <li>Verify that OpenAI API keys are correctly configured</li>
                <li>Check browser console for any JavaScript errors</li>
              </ul>
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
      <CardFooter className="flex justify-between">
        <div className="text-xs text-muted-foreground">
          Last checked: {new Date().toLocaleTimeString()}
        </div>
        {onClose && (
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        )}
      </CardFooter>
    </Card>
  );
} 